/**
 * The kinds of work task, when Rio opens several: chips to choose any of them,
 * the order they run in, the time they take together and Rio's notes.
 *
 * A chosen kind carries a number badge for its place in the order; with two
 * or more, a short list below lets the recruiter move each earlier or later
 * (buttons, so it works by keyboard and screen reader as well as by touch).
 * "Rio picks" stands on its own. A kind that doesn't open yet is "Soon" and
 * can't be added. One kind always stays chosen. Choosing and moving change the
 * config at once; what happens to the minutes is useWorkTasks.
 */
import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent,
} from 'react';
import type {
  InterviewerConfig,
  InterviewerPreview,
  WorkspaceKind,
} from '../../services/interviewer.types';
import { WORKSPACE_KINDS } from '../../services/interviewer.types';
import {
  WORKSPACE_LABELS,
  isComingSoon,
  kindsOf,
} from '../../interviewer/model';
import { AlertIcon, EarlierIcon, LaterIcon } from './icons';
import styles from './index.module.css';

interface Props {
  config: InterviewerConfig;
  preview: InterviewerPreview;
  disabled: boolean;
  /** Rio's word on a set that doesn't fit, shown beside the chips. */
  problem: string | null;
  /** A fix Rio's refusal allows, e.g. "Give it 30 minutes". */
  fix: { label: string; run: () => void } | null;
  /** What the card changed to fit the tasks, with a way to put it back. */
  adjusted: { note: string; undo: () => void } | null;
  onPick: (kind: WorkspaceKind) => void;
  onMove: (kind: WorkspaceKind, delta: -1 | 1) => void;
}

const cx = (...names: (string | false | undefined)[]) =>
  names.filter(Boolean).join(' ');

/** A short spring: it overshoots a little and settles, in about a third of a second. */
const SPRING = 'cubic-bezier(0.34, 1.4, 0.64, 1)';
const SPRING_MS = 320;

const reduceMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

const WorkTaskKinds = ({
  config,
  preview,
  disabled,
  problem,
  fix,
  adjusted,
  onPick,
  onMove,
}: Props) => {
  const helpId = useId();
  const chosen = kindsOf(config);
  const { workspace } = preview;
  const rows = useRef(new Map<WorkspaceKind, HTMLLIElement>());
  const buttons = useRef(new Map<string, HTMLButtonElement>());
  // Where each row was just before a move, for the spring; set only by touch
  // and mouse. A keyboard move repeats as the key does, so it doesn't animate.
  const before = useRef<Map<WorkspaceKind, number> | null>(null);
  const refocus = useRef<{ kind: WorkspaceKind; delta: -1 | 1 } | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const order = chosen.join();

  useLayoutEffect(() => {
    const was = before.current;
    before.current = null;
    if (!was || reduceMotion()) return;
    rows.current.forEach((row, kind) => {
      const from = was.get(kind);
      if (from === undefined || typeof row.animate !== 'function') return;
      const delta = from - row.getBoundingClientRect().top;
      if (Math.abs(delta) < 1) return;
      row.animate(
        [{ transform: `translateY(${delta}px)` }, { transform: 'none' }],
        { duration: SPRING_MS, easing: SPRING },
      );
    });
  }, [order]);

  // A move keeps focus on the button that made it, or its partner when it
  // has reached an end and can go no further.
  useEffect(() => {
    const target = refocus.current;
    if (!target) return;
    refocus.current = null;
    const index = chosen.indexOf(target.kind);
    const wanted = target.delta < 0 ? 'earlier' : 'later';
    const blocked =
      wanted === 'earlier' ? index === 0 : index === chosen.length - 1;
    const button = buttons.current.get(
      `${target.kind}:${blocked ? (wanted === 'earlier' ? 'later' : 'earlier') : wanted}`,
    );
    button?.focus();
  }, [order, chosen]);

  const move = (
    event: MouseEvent<HTMLButtonElement>,
    kind: WorkspaceKind,
    delta: -1 | 1,
  ) => {
    const index = chosen.indexOf(kind);
    if (event.detail > 0) {
      const tops = new Map<WorkspaceKind, number>();
      rows.current.forEach((row, k) =>
        tops.set(k, row.getBoundingClientRect().top),
      );
      before.current = tops;
    }
    refocus.current = { kind, delta };
    setAnnouncement(
      `${WORKSPACE_LABELS[kind]} is now task ${index + delta + 1} of ${chosen.length}.`,
    );
    onMove(kind, delta);
  };

  const tasks = workspace.tasks ?? [];
  const notes = [...tasks.map((t) => t.note), workspace.note].filter(
    (n, i, all): n is string => !!n && all.indexOf(n) === i,
  );
  const reorderable = chosen.length > 1;

  return (
    <>
      <div
        className={styles.kinds}
        role="group"
        aria-label="Kind of work task"
        aria-describedby={helpId}
      >
        {WORKSPACE_KINDS.map((kind) => {
          const index = chosen.indexOf(kind);
          const on = index >= 0;
          const soon = isComingSoon(kind, workspace.availableKinds);
          // A kind that doesn't open yet can't be added (it stays focusable,
          // so a screen reader can say why); the only kind chosen can't be
          // taken off, as one task stays.
          const blocked = soon && !on;
          const held = on && chosen.length === 1;
          // Places only mean something from two tasks, and not for Rio's pick.
          const numbered = on && kind !== 'auto' && chosen.length > 1;
          const name = WORKSPACE_LABELS[kind];
          return (
            <button
              key={kind}
              type="button"
              aria-pressed={on}
              aria-disabled={blocked || held || undefined}
              aria-label={
                blocked
                  ? `${name}, coming soon, not available`
                  : numbered
                    ? `${name}, task ${index + 1} of ${chosen.length}${soon ? ', coming soon' : ''}`
                    : undefined
              }
              className={cx(
                styles.chip,
                on && styles.chipMore,
                held && styles.chipHeld,
                blocked && styles.chipBlocked,
              )}
              disabled={disabled}
              onClick={() => {
                if (!held && !blocked) onPick(kind);
              }}
            >
              {numbered && (
                <span className={styles.chipBadge} aria-hidden>
                  {index + 1}
                </span>
              )}
              {name}
              {soon && <span className={styles.soon}>Soon</span>}
            </button>
          );
        })}
      </div>
      <div className={styles.kindsProblem} aria-live="polite">
        {problem && (
          <>
            <span className={styles.kindsProblemMark}>
              <AlertIcon />
            </span>
            <span className={styles.kindsProblemText}>
              {problem}
              {fix && (
                <>
                  {' '}
                  <button
                    type="button"
                    className={styles.textButton}
                    disabled={disabled}
                    onClick={fix.run}
                  >
                    {fix.label}
                  </button>
                </>
              )}
            </span>
          </>
        )}
      </div>
      {adjusted && (
        <p className={styles.kindsAdjusted}>
          <span>{adjusted.note}</span>
          <button
            type="button"
            className={styles.textButton}
            disabled={disabled}
            onClick={adjusted.undo}
          >
            Undo
          </button>
        </p>
      )}
      <p className={styles.hint} id={helpId}>
        Choose as many as you like, or let Rio pick one. They run in the order
        you set, and at least one stays.
      </p>

      {reorderable && (
        <ol
          className={styles.taskOrder}
          role="list"
          aria-label="The order the work tasks run in"
        >
          {chosen.map((kind, i) => {
            const label = WORKSPACE_LABELS[kind];
            const place = `${label}, task ${i + 1} of ${chosen.length}`;
            const task = tasks.find((t) => t.kind === kind);
            const opens = !!task;
            const minutesFor = task?.minutes ?? 0;
            return (
              <li
                key={kind}
                className={styles.taskRow}
                ref={(row) => {
                  if (row) rows.current.set(kind, row);
                  else rows.current.delete(kind);
                }}
              >
                <span className={styles.orderBadge} aria-hidden>
                  {i + 1}
                </span>
                <span className={styles.taskName}>{label}</span>
                <span className={styles.taskMinutes}>
                  {/* As in the total: a kind Rio has no task for here adds nothing. */}
                  {opens ? `${minutesFor} min` : 'No task for this role'}
                </span>
                <span className={styles.taskMove}>
                  {(['earlier', 'later'] as const).map((way) => {
                    const delta = way === 'earlier' ? -1 : 1;
                    const end =
                      way === 'earlier' ? i === 0 : i === chosen.length - 1;
                    return (
                      <button
                        key={way}
                        type="button"
                        className={styles.iconButton}
                        aria-label={`${place}, move ${way}`}
                        disabled={disabled || end}
                        ref={(button) => {
                          if (button)
                            buttons.current.set(`${kind}:${way}`, button);
                          else buttons.current.delete(`${kind}:${way}`);
                        }}
                        onClick={(event) => move(event, kind, delta)}
                      >
                        {way === 'earlier' ? <EarlierIcon /> : <LaterIcon />}
                      </button>
                    );
                  })}
                </span>
              </li>
            );
          })}
        </ol>
      )}

      {tasks.length > 0 && workspace.tasksMinutes !== null && (
        <p className={styles.taskTotal} aria-live="polite">
          <span className={styles.taskTotalValue}>
            {workspace.tasksMinutes} min
          </span>{' '}
          for {plural(tasks.length, 'task', 'tasks')}
        </p>
      )}
      {notes.map((note) => (
        <p key={note} className={styles.hint}>
          {note}
        </p>
      ))}
      <p className={styles.srOnly} aria-live="polite">
        {announcement}
      </p>
    </>
  );
};

export default WorkTaskKinds;
